import React from 'react';
import { Box, Card, CardContent, Typography, Button } from '@mui/joy';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMeeting } from '../../../../context/MeetingContext';
import MeetingDatePicker from './MeetingDatePicker';
import MeetingTimeWindow from './MeetingTimeWindow';
import MeetingAddressInput from './MeetingAddressInput';
import MeetingNotesInput from './MeetingNotesInput';
import MeetingTimezoneSelector from './MeetingTimezoneSelector';
import SelectedCandidatesAvatars from './SelectedCandidatesAvatars';
import { upsertMeeting } from 'apiconfig/api';
import { useSession } from 'context/SessionContext';

const MeetingStepOne = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const meeting = useMeeting();
  const { sessionEmployerId, isLoggedIn } = useSession();
  
  // Get person IDs from URL query parameters - memoize to prevent recreating array
  const applicantIdsParam = searchParams.get('applicantIds');
  const selectedApplicantIds = React.useMemo(() => {
    return applicantIdsParam ? applicantIdsParam.split(',').filter(id => id.trim()) : [];
  }, [applicantIdsParam]);

  React.useEffect(() => {
    if (selectedApplicantIds.length > 0) {
      meeting.setApplicantIds(selectedApplicantIds);
    }
  }, [selectedApplicantIds, meeting.setApplicantIds]);

  const getNextQuarterHour = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const nextQuarter = Math.ceil(minutes / 15) * 15;
    const nextTime = new Date(now);
    nextTime.setMinutes(nextQuarter, 0, 0);
    return nextTime;
  };

  const getEndTimeFromStart = (start: Date, durationMs: number) => {
    return new Date(start.getTime() + durationMs);
  };

  const [selectedDate, setSelectedDate] = React.useState<Date>(meeting.scheduledAt);
  const [startTimePicker, setStartTimePicker] = React.useState<Date>(getNextQuarterHour());
  const [endTimePicker, setEndTimePicker] = React.useState<Date>(getEndTimeFromStart(getNextQuarterHour(), meeting.duration));
  const [timeError, setTimeError] = React.useState<string>('');
  const [addressError, setAddressError] = React.useState<string>('');
  const [dateError, setDateError] = React.useState<string>('');

  // Validate address whenever it changes
  React.useEffect(() => {
    if (!meeting.location || meeting.location.trim() === '') {
      setAddressError('Meeting address is required.');
    } else {
      setAddressError('');
    }
  }, [meeting.location]);

  // Validate date whenever it changes
  React.useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of today
    
    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setHours(0, 0, 0, 0); // Set to start of selected date
    
    if (selectedDateStart < today) {
      setDateError('Meeting date cannot be in the past.');
    } else {
      setDateError('');
    }
  }, [selectedDate]);

  React.useEffect(() => {
    const newStartTime = new Date(selectedDate);
    newStartTime.setHours(startTimePicker.getHours(), startTimePicker.getMinutes(), 0, 0);

    let newEndTime = new Date(selectedDate);
    newEndTime.setHours(endTimePicker.getHours(), endTimePicker.getMinutes(), 0, 0);

    if (
      endTimePicker.getHours() < startTimePicker.getHours() ||
      (endTimePicker.getHours() === startTimePicker.getHours() && endTimePicker.getMinutes() <= startTimePicker.getMinutes())
    ) {
      newEndTime.setDate(newEndTime.getDate() + 1);
    }

    meeting.setScheduledAt(newStartTime);
    meeting.setDuration(newEndTime.getTime() - newStartTime.getTime());

    // Check if the meeting time is in the past
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setHours(0, 0, 0, 0);
    
    let timeErrorMessage = '';
    
    // If it's today and the start time is in the past (with buffer)
    if (selectedDateStart.getTime() === today.getTime() && newStartTime <= now) {
      timeErrorMessage = 'Meeting time must be at least 15 minutes in the future.';
    } else {
      // Check minimum duration
      const timeDiff = newEndTime.getTime() - newStartTime.getTime();
      const hoursDiff = timeDiff / (1000 * 60 * 60);

      if (hoursDiff < 2) {
        timeErrorMessage = 'Time window must be at least 2 hours.';
      }
    }
    
    setTimeError(timeErrorMessage);
  }, [selectedDate, startTimePicker, endTimePicker, meeting.setScheduledAt, meeting.setDuration]);

  const handleStartTimeChange = (date: Date) => {
    setStartTimePicker(date);
    let currentDuration;
    if (endTimePicker.getTime() < startTimePicker.getTime()) {
      const tempEnd = new Date(endTimePicker);
      tempEnd.setDate(tempEnd.getDate() + 1);
      currentDuration = tempEnd.getTime() - startTimePicker.getTime();
    } else {
      currentDuration = endTimePicker.getTime() - startTimePicker.getTime();
    }
    const newEndTime = new Date(date.getTime() + currentDuration);
    setEndTimePicker(newEndTime);
  };

  const handleButtonClick = async () => {
    if (!isLoggedIn || !sessionEmployerId) {
      navigate('/auth/signup?newEmployer=true&redirect=/hiring/meetings/save');
    } else {
      try {
        await persistMeeting();
        navigate(`/hiring/meetings/${meeting.id}/success`);
      } catch (error) {
        console.error('Failed to persist meeting:', error);
      }
    }
  }

  // Persist a meeting after payment confirmation
  const persistMeeting = async () => {
    // 1. Get meetingId and meetingPayload from context
    const meetingId = meeting.id;
    // 2. Get employerId from session context
    const employerId = sessionEmployerId;
    if (!employerId) throw new Error('No employer ID in session');
    // 3. Prepare meeting payload from context
    const meetingPayload = {
      scheduledAt: meeting.scheduledAt.toISOString(),
      duration: meeting.duration,
      location: meeting.location,
      notes: meeting.notes,
      timezone: meeting.timezone,
      applicantIds: meeting.applicantIds,
    };
    await upsertMeeting(employerId, meetingId, meetingPayload);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: 4 }}>
      <Card sx={{ maxWidth: 500, mx: 'auto', p: 3 }}>
        <CardContent>
          <Typography level="h4" mb={1}>Schedule Meeting</Typography>
          <Typography level="body-md" mb={2} color="neutral">
            Set your available time window and address for in-person meetings with selected people.
          </Typography>

          <MeetingDatePicker value={selectedDate} onChange={setSelectedDate} />
          {dateError && (
            <Typography level="body-sm" color="danger" sx={{ mt: 1, mb: 2 }}>
              {dateError}
            </Typography>
          )}

          <MeetingTimeWindow
            startTime={startTimePicker}
            endTime={endTimePicker}
            onStartChange={handleStartTimeChange}
            onEndChange={setEndTimePicker}
            error={timeError}
          />

          <MeetingTimezoneSelector
            value={meeting.timezone}
            onChange={meeting.setTimezone}
          />

          <MeetingAddressInput
            value={meeting.location}
            onChange={e => meeting.setLocation(e.target.value)}
            error={addressError}
          />

          <MeetingNotesInput
            value={meeting.notes}
            onChange={e => meeting.setNotes(e.target.value)}
          />

          <SelectedCandidatesAvatars applicantIds={meeting.applicantIds} />

          <Button
            fullWidth 
            variant="solid" 
            sx={{ mt: 2 }} 
            disabled={!!timeError || !!addressError || !!dateError || !meeting.timezone}
            onClick={handleButtonClick}
          >
            Continue
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

const MeetingsNew = () => (
  <MeetingStepOne />
);

export default MeetingsNew;